// NPM
import React, { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  TextField,
  Menu,
  MenuItem,
  Button,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';

// COMPONENTS
import SingleInvoice from '../../components/billing/invoices/show/SingleInvoice';
import UserUsage from '../../components/billing/invoices/show/UserUsage';
import FullUsage from '../../components/billing/invoices/show/FullUsage';
import InvoiceDetails from '../../components/billing/invoices/show/InvoiceDetails';
import CustomModal from '../../components/common/CustomModal';
import StagedModal from '../../components/common/StagedModal';

// STYLING
import InvoiceShowFormattedMessages from '../../messages/billing/InvoiceShowFormattedMessages';
// import '../../Billing.css';

// NETWORKING
import useInvoiceRequest from '../../requests/InvoiceRequest';
import { ArrowBack, ChevronLeft } from '@mui/icons-material';
import Loader from '../../components/common/Loader';

const InvoiceShow = (props) => {
  const { customer, tab } = useParams();
  const {
    getSingleInvoice,
    getUserInvoiceDetails,
    getInvoiceDetails,
    getFullInvoiceDetails,
    updateStatus,
    processInvoiceTransaction,
    getXeroInformation,
    voidXeroInvoice,
  } = useInvoiceRequest();

  let dataLoaded = false;
  const [stage, setStage] = useState(0);
  const [showStaged, setShowStaged] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalParameters, setModalParams] = useState({});
  const [printCheck, setPrintCheck] = useState(false);

  const [details, setDetails] = useState('1');
  const [invoice, setInvoice] = useState(null); // Single Invoice object
  const [XeroInfo, setXeroInfo] = useState(null);
  const [fullUsageData, setFullUsageData] = useState(null); // Full Usage View
  const [userUsageData, setUserUsageData] = useState(null); // User Usage View
  const [invoiceDetailsData, setInvoiceDetailsData] = useState(null); // Invoice Details View

  const [loadingUser, setLoadingUser] = useState(true); // Is Loading User Usage Details
  const [loadingFull, setLoadingFull] = useState(true); // Is Loading Full Details

  const [statusAnchorEl, setStatusAnchorEl] = React.useState(null);
  const openStatusMenu = Boolean(statusAnchorEl);
  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };
  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  useEffect(() => {
    async function getData() {
      if (customer && tab) {
        let singleInvoiceData = await getSingleInvoice(
          customer,
          tab,
          props.errorHandler
        );

        const urlParams = new URLSearchParams(window.location.search);

        setPrintCheck(urlParams.get('print'));
        setInvoice(singleInvoiceData);
        setLoadingUser(false);
        setLoadingFull(false);
      }
    }

    getData();
  }, [customer, tab, props.errorHandler]);

  useEffect(() => {
    if (printCheck && dataLoaded && !loadingUser && !loadingFull) {
      setPrintCheck(false);
      window.setTimeout(printAfterRender, 1000);
    }
  }, [printCheck, dataLoaded, loadingUser, loadingFull]);

  const getDetails = useCallback(async () => {
    let invoiceDetailsData = await getInvoiceDetails(
      customer,
      tab,
      props.errorHandler
    );
    setInvoiceDetailsData(invoiceDetailsData);
  }, []);

  const getUserUsageDetails = useCallback(async () => {
    const userInvoiceData = await getUserInvoiceDetails(
      customer,
      tab,
      props.errorHandler
    );
    console.log('userUsageData:', userInvoiceData);
    setUserUsageData(userInvoiceData);
  }, []);

  const getFullUsageDetails = useCallback(async () => {
    const detailedInvoiceData = await getFullInvoiceDetails(
      customer,
      tab,
      props.errorHandler
    );
    console.log('fullUsageData:', detailedInvoiceData);
    setFullUsageData(detailedInvoiceData);
  }, []);

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (name === 'details') setDetails(value);
  };

  const performAction = async (customerId, providerId, billId) => {
    console.log('performAction');
    switch (modalParameters.actionType) {
      case 'processPendingInvoice':
        console.log('processPendingInvoice');
        await processPendingInvoice(billId, customerId);
        break;
      case 'transitionPendingToInvoiced':
        console.log('transitionPendingToInvoiced');
        await transitionPendingToInvoiced(customerId, providerId, billId);
        break;
      case 'transitionBillToFailed':
        console.log('transitionBillToFailed');
        await transitionBillToFailed(customerId, providerId, billId);
        break;
      case 'cancelPendingTransaction':
        console.log('cancelPendingTransaction');
        await cancelPendingTransaction(customerId, providerId, billId);
        break;
      case 'transitionInvoicedToPending':
        console.log('transitionInvoicedToPending');
        await transitionInvoicedToPending(customerId, providerId, billId);
        break;
      case 'voidXeroInvoice':
        console.log('voidXeroInvoice');
        await voidXeroInvoice(customerId, providerId, billId);
        break;
      default:
        console.error('Invoice attempted to perform an unknown action');
        break;
    }
  };

  const processPendingInvoice = async (billId, customerId) => {
    if (!showStaged) {
      setStage(0);
      setShowStaged(true);
      setModalParams({
        actionType: 'processPendingInvoice',
        billId,
        customerId,
        stageTitle: 'Invoice Bill',
        stageConfirmation:
          'Are you sure you would like to process payment on this invoice?',
        stagePending: 'Processing transaction...',
        stageSuccess: 'Invoice paid successfully!',
        stageFailure: 'Invoice payment failed!',
      });
    } else if (stage === 0) {
      setStage(1);
      let results = await processInvoiceTransaction(
        modalParameters?.customerId,
        modalParameters?.billId,
        props.errorHandler
      );
      if (results.message === 'Successful Payment') {
        setStage(2);
        setInvoice({
          ...invoice,
          billingStatus: 'Paid',
          successfulPayment: results.successfulPayment,
        });
      } else {
        setStage(3);
        if (results.failedPayments)
          setInvoice({ ...invoice, failedPayments: results.failedPayments });
        setModalParams({
          stageTitle: 'Invoice Bill',
          errorMsg: results.message,
          stageFailure: 'Invoice payment failed!',
        });
      }
      console.log(results);
    }
  };

  const transitionBillToFailed = async (billId, customerId, providerId) => {
    if (!showStaged) {
      setStage(0);
      setShowStaged(true);
      setModalParams({
        actionType: 'transitionBillToFailed',
        billId,
        customerId,
        providerId,
        stageTitle: 'Fail Transaction',
        stageConfirmation: 'Are you sure you want to fail this invoice?',
        stagePending: 'Failing Invoice...',
        stageSuccess: 'Invoice successfully transitioned to "Failed"!',
        stageFailure: 'Invoice failed to transition to "Failed"!',
      });
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'F', //Failed
        props.errorHandler
      );
      console.log('got results updating invoice:', result);

      if (result.status === 'Failed') {
        setStage(2);
        if (result.status)
          setInvoice({ ...invoice, billingStatus: result.status });
      } else setStage(3);
    }
  };

  const cancelPendingTransaction = async (billId, customerId, providerId) => {
    if (!showStaged) {
      setStage(0);
      setShowStaged(true);
      setModalParams({
        actionType: 'cancelPendingTransaction',
        billId,
        customerId,
        providerId,
        stageTitle: 'Cancel Transaction',
        stageConfirmation: 'Are you sure you want to cancel this invoice?',
        stagePending: 'Canceling Invoice...',
        stageSuccess: 'Invoice Cancelled successfully!',
        stageFailure: 'Invoice failed to be cancelled!',
      });
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'C', //Cancelled
        props.errorHandler
      );
      console.log('got results updating invoice:', result);

      if (result.status === 'Cancelled') {
        setStage(2);
        if (result.status)
          setInvoice({ ...invoice, billingStatus: result.status });
      } else setStage(3);
    }
  };

  const checkXeroInvoice = async (invoiceNo) => {
    setXeroInfo(null);
    setShowModal(true);
    let result = await getXeroInformation(invoiceNo, props.errorHandler);
    console.log('Xero Info', result);
    if (!result || result.error) setXeroInfo('missing');
    else setXeroInfo(result);
  };

  const xeroInformationModal = () => {
    let message = 'Loading...';
    if (XeroInfo && XeroInfo !== 'missing') {
      message = JSON.stringify(XeroInfo, null, 4);
      message = message.replace(/(\\r\\n)/g, `\n\t`);
    } else if (XeroInfo === 'missing')
      message =
        'There was no invoice or credit note found that corresponds with this invoice.';

    const { billingStatus } = invoice;
    const voidButtonEnabled =
      (billingStatus !== 'Cancelled' && billingStatus !== 'Failed') ||
      !XeroInfo ||
      XeroInfo === 'missing' ||
      XeroInfo?.status?.toLowerCase() === 'voided';

    return (
      <CustomModal
        title="Xero Invoice Information"
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <Typography gutterBottom>{message}</Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContents: 'end' }}>
          <Button
            disabled={voidButtonEnabled}
            variant="contained"
            color="secondary"
            onClick={handleVoidXeroInvoice}
          >
            Void Xero Invoice
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
        </Box>
      </CustomModal>
    );
  };

  const handleVoidXeroInvoice = async () => {
    setShowModal(false);
    if (!showStaged) {
      setStage(0);
      setShowStaged(true);
      setModalParams({
        actionType: 'voidXeroInvoice',
        stageTitle: 'Void Xero Invoice',
        stageConfirmation: 'Are you sure you want to void this invoice?',
        stagePending: 'Voiding Invoice...',
        stageSuccess: 'Invoice Voided successfully!',
        stageFailure: 'Invoice failed to be voided!',
      });
    } else if (stage === 0) {
      setStage(1);
      let result = await voidXeroInvoice(tab, props.errorHandler);
      console.log('void result', result);

      if (!result?.error) {
        setStage(2);
      } else setStage(3);
    }
  };

  const transitionPendingToInvoiced = async (
    billId,
    customerId,
    providerId
  ) => {
    if (!showStaged) {
      setStage(0);
      setShowStaged(true);
      setModalParams({
        actionType: 'transitionPendingToInvoiced',
        billId,
        customerId,
        providerId,
        stageTitle: 'Invoice Bill',
        stageConfirmation:
          'Are you sure you would like convert this pending bill to invoiced?',
        stagePending: 'Invoicing bill...',
        stageSuccess: 'Invoice successfully transitioned to "Invoiced"!',
        stageFailure: 'Invoice failed to transition to "Invoiced"!',
      });
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'I', //Invoiced
        props.errorHandler
      );
      console.log('got results updating invoice:', result);

      if (result.status === 'Invoiced') {
        setStage(2);
        if (result.status)
          setInvoice({ ...invoice, billingStatus: result.status });
      } else setStage(3);
    }
  };

  const transitionInvoicedToPending = async (
    billId,
    customerId,
    providerId
  ) => {
    if (!showStaged) {
      setStage(0);
      setShowStaged(true);
      setModalParams({
        actionType: 'transitionInvoicedToPending',
        billId,
        customerId,
        providerId,
        stageTitle: 'Pend Bill',
        stageConfirmation:
          'Are you sure you would like convert this invoiced bill to pending?',
        stagePending: 'Pending bill...',
        stageSuccess: 'Invoice successfully transitioned to "Pending"!',
        stageFailure: 'Invoice failed to transition to "Pending"!',
      });
    } else if (stage === 0) {
      setStage(1);
      let result = await updateStatus(
        modalParameters?.providerId,
        modalParameters?.customerId,
        modalParameters?.billId,
        'N', //Pending
        props.errorHandler
      );
      console.log('got results updating invoice:', result);

      if (result.status === 'Pending') {
        setStage(2);
        if (result.status)
          setInvoice({ ...invoice, billingStatus: result.status });
      } else setStage(3);
    }
  };

  const backToBilling = () => {
    props.navigate(-1);
  };

  const getDisplayedContent = () => {
    console.log('Viewing invoice show page:', details);

    switch (details) {
      case '1':
        return (
          <SingleInvoice
            errorHandler={props.errorHandler}
            invoice={invoice}
            backToBilling={backToBilling}
          />
        );
      case '2':
        return (
          <InvoiceDetails
            errorHandler={props.errorHandler}
            invoice={invoice}
            invoiceDetailsData={invoiceDetailsData}
            startDate={invoice.bill_dates.start_date}
            endDate={invoice.bill_dates.end_date}
            getDataFunction={getDetails}
          />
        );
      case '3':
        return (
          <UserUsage
            invoice={invoice}
            errorHandler={props.errorHandler}
            userUsageData={userUsageData}
            getDataFunction={getUserUsageDetails}
            billDates={invoice.bill_dates}
          />
        );
      case '4':
        return (
          <FullUsage
            invoice={invoice}
            errorHandler={props.errorHandler}
            fullUsageData={fullUsageData}
            getDataFunction={getFullUsageDetails}
            billDates={invoice.bill_dates}
          />
        );
      default:
        return <Typography>page selection error</Typography>;
    }
  };

  const printAfterRender = () => {
    window.print();
  };

  const generateLoadingFragment = () => {
    return <Loader />;
  };

  const generateInvoiceDropdown = () => {
    let dropdown = (
      <Menu
        id="status-menu"
        anchorEl={statusAnchorEl}
        open={openStatusMenu}
        onClose={handleStatusClose}
        MenuListProps={{
          'aria-labelledby': 'status-button',
        }}
      >
        <MenuItem>{InvoiceShowFormattedMessages.loading}</MenuItem>
      </Menu>
    );
    const { accessLevel, accessType } = props;
    const isGodAdmin = accessLevel === '3' && accessType === '3';
    let isPostpaid = false;

    if (invoice) {
      const { billingStatus, providerId, customerType } = invoice;
      let cancelEntry;
      if (customerType === 'B') {
        cancelEntry = (
          <MenuItem
            onClick={() => cancelPendingTransaction(tab, customer, providerId)}
          >
            {InvoiceShowFormattedMessages.cancelTransaction}
          </MenuItem>
        );
      } else {
        isPostpaid = true;
      }

      if (billingStatus === 'Pending') {
        dropdown = (
          <Menu
            id="status-menu"
            anchorEl={statusAnchorEl}
            open={openStatusMenu}
            onClose={handleStatusClose}
            MenuListProps={{
              'aria-labelledby': 'status-button',
            }}
          >
            <MenuItem onClick={() => processPendingInvoice(tab, customer)}>
              {InvoiceShowFormattedMessages.processTransaction}
            </MenuItem>
            {cancelEntry}
            {isGodAdmin && (
              <Fragment>
                <MenuItem
                  onClick={() =>
                    transitionPendingToInvoiced(tab, customer, providerId)
                  }
                >
                  {InvoiceShowFormattedMessages.transitionToInvoiced}
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    transitionBillToFailed(tab, customer, providerId)
                  }
                >
                  {InvoiceShowFormattedMessages.failTransaction}
                </MenuItem>
              </Fragment>
            )}
          </Menu>
        );
      } else if (billingStatus === 'Invoiced' && isGodAdmin) {
        dropdown = (
          <Menu
            id="status-menu"
            anchorEl={statusAnchorEl}
            open={openStatusMenu}
            onClose={handleStatusClose}
            MenuListProps={{
              'aria-labelledby': 'status-button',
            }}
          >
            <MenuItem
              onClick={() =>
                transitionInvoicedToPending(tab, customer, providerId)
              }
            >
              {InvoiceShowFormattedMessages.transitionToPending}
            </MenuItem>
            <MenuItem
              onClick={() => transitionBillToFailed(tab, customer, providerId)}
            >
              {InvoiceShowFormattedMessages.failTransaction}
            </MenuItem>
            {cancelEntry}
          </Menu>
        );
      } else {
        dropdown = (
          <Menu
            id="status-menu"
            anchorEl={statusAnchorEl}
            open={openStatusMenu}
            onClose={handleStatusClose}
            MenuListProps={{
              'aria-labelledby': 'status-button',
            }}
          >
            <MenuItem>{invoice?.billingStatus}</MenuItem>
          </Menu>
        );
      }
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>
            {InvoiceShowFormattedMessages.selectLevelDetailsDisplay}:
          </Typography>

          <TextField
            onChange={(e) => handleInputChange(e)}
            select
            id="details"
            name="details"
            className="tableSearch"
            size="small"
            value={details}
            disabled={!isPostpaid}
          >
            <MenuItem defaultValue value="1">
              Show Invoice
            </MenuItem>
            <MenuItem value="2">Invoice Details</MenuItem>
            <MenuItem value="3">User Usage Per Day</MenuItem>
            <MenuItem value="4">Full Usage Details</MenuItem>
          </TextField>

          <Typography>Bill Status:</Typography>

          <Button
            id="status-button"
            variant="contained"
            color="secondary"
            endIcon={<ChevronLeft sx={{ rotate: '-90deg' }} />}
            aria-controls={openStatusMenu ? 'status-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openStatusMenu ? 'true' : undefined}
            onClick={handleStatusClick}
            sx={{ paddingRight: 2 }}
          >
            {invoice.billingStatus}
          </Button>
          {dropdown}
        </Box>
        {generateXeroButton()}
      </Box>
    );
  };

  const generateXeroButton = () => {
    console.log('invoice', invoice);
    const { accessLevel, accessType } = props;
    if (accessLevel === '3' && accessType === '3') {
      return (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => checkXeroInvoice(invoice.invoice_no)}
        >
          Check Xero Invoice
        </Button>
      );
    }
  };

  const generateBackButton = () => {
    return (
      <Button startIcon={<ArrowBack />} onClick={backToBilling} variant="link">
        Back
      </Button>
    );
  };

  dataLoaded = true;
  return (
    <Fragment>
      {loadingUser || loadingFull ? (
        generateLoadingFragment()
      ) : (
        <Container sx={{ paddingTop: 2, paddingBottom: 4 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              {generateBackButton()}
              {generateInvoiceDropdown()}
              <br />
              <hr />
              <br />
              {getDisplayedContent()}
            </Grid>
          </Grid>
          {xeroInformationModal()}
          <StagedModal
            showStaged={showStaged}
            removeStaged={() => {
              setShowStaged(false), setModalParams({});
            }}
            removeStagedSlow={() => setStage(0)}
            stage={stage}
            performAction={performAction}
            stageTitle={modalParameters?.stageTitle}
            stageConfirmation={modalParameters?.stageConfirmation}
            stagePending={modalParameters?.stagePending}
            stageSuccess={modalParameters?.stageSuccess}
            stageFailure={
              modalParameters?.stageFailure +
              (modalParameters?.errorMsg ? ': ' + modalParameters.errorMsg : '')
            }
            errorHandler={props.errorHandler}
          />
        </Container>
      )}
    </Fragment>
  );
};

export default InvoiceShow;
